import { useLayoutEffect, useRef, useState, type CSSProperties, type RefObject } from "react";
import { keepInView } from "../lib/popover";

/** Popovers become bottom sheets at this width (matches workspace.css), which are always on screen. */
const SHEET = "(max-width: 720px)";

/**
 * Keeps a popover within the part of the preview that's on screen: it moves up or down, and scrolls
 * inside when it's taller than the space. `key` changes whenever where it sits or what it shows does.
 * Returns the style to add, and a function that puts a shift in front of the popover's own transform.
 */
export function useKeepInView(popover: RefObject<HTMLElement | null>, key: string): { style: CSSProperties; transform: (own?: string) => string | undefined } {
  const [fit, setFit] = useState<{ shift: number; maxHeight?: number }>({ shift: 0 });
  const applied = useRef(0);

  useLayoutEffect(() => {
    const element = popover.current;
    if (!element || window.matchMedia(SHEET).matches) {
      applied.current = 0;
      setFit({ shift: 0 });
      return;
    }
    const area = element.closest<HTMLElement>(".bw-frame")?.getBoundingClientRect();
    const screenBottom = window.visualViewport?.height ?? window.innerHeight;
    const visibleTop = Math.max(area?.top ?? 0, 0);
    const visibleBottom = Math.min(area?.bottom ?? screenBottom, screenBottom);
    // Measured without the shift already applied, so repeated measuring doesn't drift.
    const next = keepInView(element.getBoundingClientRect().top - applied.current, element.scrollHeight, visibleTop, visibleBottom);
    applied.current = next.shift;
    setFit((now) => now.shift === next.shift && now.maxHeight === next.maxHeight ? now : next);
  }, [popover, key]);

  return {
    style: fit.maxHeight ? { maxHeight: fit.maxHeight, overflowY: "auto" } : {},
    transform: (own) => [fit.shift ? `translateY(${fit.shift}px)` : "", own ?? ""].filter(Boolean).join(" ") || undefined,
  };
}
